import { Input, InputRef, Tooltip } from "antd-v5";
import { TooltipPlacement } from "antd-v5/es/tooltip";
import classNames from "classnames";
import InternalLink from "components/InternalLink";
import { isFunction, isString, isUndefined, noop, omit } from "lodash";
import React, {
  MouseEvent,
  ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import ACTrigger, { ACTriggerProps } from "ac-components/ACTrigger";
import ChevronDownIcon from "ac-components/ac-icons/ChevronDownIcon";
import ChevronRightIcon from "ac-components/ac-icons/ChevronRightIcon";
import ChevronUpIcon from "ac-components/ac-icons/ChevronUpIcon";

import "./index.less";

type ACDropdownFilterOption = (
  inputValue: string,
  option: ACDropdownOptionProps,
) => boolean;

export type ACDropdownOptionProps =
  | ACDropdownItemProps
  | ACDropdownDividerProps
  | ACDropdownLabelProps;

export type ACDropdownItemProps = {
  label: React.ReactNode;
  value: string | number;
  labelString?: string;
  filterOption?: ACDropdownFilterOption;
  icon?: React.ReactNode;
  secondaryLabel?: React.ReactNode;
  secondaryIcon?: React.ReactNode;
  disabled?: boolean;
  to?: string;
  items?: ACDropdownOptionProps[];
  subMenuWidth?: number;
  tooltip?: ReactNode;
  tooltipPlacement?: TooltipPlacement;
  hidden?: boolean;
};

type ACDropdownItemPrivateProps = {
  onClick: (value: string | number) => void;
};

type ACDropdownDividerProps = {
  type: "divider";
  filterOption?: ACDropdownFilterOption;
};
type ACDropdownLabelProps = {
  type: "label";
  label: string;
  filterOption?: ACDropdownFilterOption;
};

type ItemLayoutType = "horizontal" | "vertical";

export type ACDropdownProps = {
  children: React.ReactNode;
  layout?: ItemLayoutType;
  items: ACDropdownOptionProps[];
  onOptionClick?: ACDropdownItemPrivateProps["onClick"];
  trigger?: ACTriggerProps["trigger"];
  placement?: ACTriggerProps["placement"];
  getPopupContainer?: ACTriggerProps["getPopupContainer"];
  offsetX?: ACTriggerProps["offsetX"];
  offsetY?: ACTriggerProps["offsetY"];
  menuWidth?: number;
  onPopupVisibleChange?: ACTriggerProps["onPopupVisibleChange"];
  autoFocus?: boolean;
  allowSearch?: boolean;
  closeOnSelect?: boolean;
  showArrow?: boolean;
  triggerClassName?: string;
  mini?: boolean;
  disabled?: boolean;
  popupVisible?: boolean;
  onSearchTextChange?: (value: string) => void;
  filterOption?: ACDropdownFilterOption;
};

const stopPropagation = (e: MouseEvent<HTMLDivElement>) => e.stopPropagation();

export type ACDropdownRef = {
  propagateKeyDown: (e: React.KeyboardEvent) => void;
};

const ACDropdown = forwardRef<ACDropdownRef, ACDropdownProps>(
  function ACDropdownWithoutRef(
    {
      children,
      layout = "horizontal",
      items,
      onOptionClick,
      placement = "bottomLeft",
      trigger = "click",
      getPopupContainer,
      offsetX,
      offsetY,
      menuWidth,
      onPopupVisibleChange = noop,
      autoFocus = true,
      allowSearch,
      closeOnSelect = true,
      showArrow,
      triggerClassName,
      mini,
      disabled,
      popupVisible: popupVisibleProp,
      onSearchTextChange,
      filterOption,
    },
    ref,
  ) {
    const inputRef = useRef<InputRef>(null);
    if (process.env.NODE_ENV === "development" && allowSearch) {
      items.forEach((item) => {
        if ("label" in item) {
          const _item = item as ACDropdownItemProps;
          if (!isString(_item.label) && !_item.labelString) {
            throw new Error(
              `ACDropdown allowSearch enabled but label is not string or labelString not passed. Either pass label as string or pass labelString for ${_item.value}.`,
            );
          }
        }
      });
    }
    const [popupVisible, setPopupVisible] = useState(popupVisibleProp || false);
    const [highlightedIndex, setHighlightedIndex] = useState(0);
    const [searchText, setSearchText] = useState("");

    const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
    const containerRef = useRef<HTMLDivElement | null>();

    const trimmedSearchText = searchText.trim().toLowerCase();
    const showSearch =
      allowSearch && (items.length > 5 || !!onSearchTextChange);

    useEffect(() => {
      if (!isUndefined(popupVisibleProp)) {
        setPopupVisible(popupVisibleProp);
      }
    }, [popupVisibleProp]);

    const _onOptionClick = useCallback<ACDropdownItemPrivateProps["onClick"]>(
      (key) => {
        setSearchText("");
        onSearchTextChange?.("");
        if (closeOnSelect) {
          setPopupVisible(false);
        }
        if (isFunction(onOptionClick)) {
          onOptionClick(key);
        }
      },
      [onOptionClick, closeOnSelect, onSearchTextChange],
    );

    const itemsToRender = useMemo<ACDropdownOptionProps[]>(() => {
      const isVisibleItem = (item: ACDropdownOptionProps) =>
        "hidden" in item && !("type" in item) ? !item.hidden : true;

      const visibleItems = items.filter(isVisibleItem);

      if (trimmedSearchText.length > 0) {
        return visibleItems.filter((item) =>
          matchesSearchFilter(trimmedSearchText, item, filterOption),
        );
      }

      return visibleItems;
    }, [filterOption, items, trimmedSearchText]);

    useEffect(() => {
      setHighlightedIndex(getNextOptionIndex(-1, itemsToRender));
    }, [itemsToRender, popupVisible, autoFocus]);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        //preventing event bubbling to parent dropdown when sub-menu/child-menu is focused
        e.stopPropagation();
        if (e.key === "ArrowDown") {
          // Move to the next enabled item
          setHighlightedIndex((prev) =>
            getNextOptionIndex(prev, itemsToRender),
          );
          e.preventDefault();
        } else if (e.key === "ArrowUp") {
          // Move to the previous enabled item
          setHighlightedIndex((prev) =>
            getPrevOptionIndex(prev, itemsToRender),
          );
          e.preventDefault();
        } else if (e.key === "Enter" && popupVisible) {
          const selectedItem = itemsToRender[highlightedIndex];
          if (selectedItem && (selectedItem as ACDropdownItemProps).value) {
            _onOptionClick((selectedItem as ACDropdownItemProps).value);
          }
          e.preventDefault();
        }
      },
      [highlightedIndex, itemsToRender, popupVisible, _onOptionClick],
    );

    function onMouseDown(e: MouseEvent<HTMLDivElement>) {
      // This will prevent closing the AC-Trigger if interaction happen on
      // ac-dropdown element
      e.preventDefault();
    }

    useEffect(() => {
      if (containerRef.current && itemRefs.current[highlightedIndex]) {
        const highlightedItem = itemRefs.current[highlightedIndex];
        if (highlightedItem) {
          highlightedItem.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
          });
        }
      }
    }, [highlightedIndex]);

    const child = isString(children)
      ? children
      : (React.Children.only(children) as React.ReactElement<unknown>);

    useImperativeHandle(
      ref,
      () => ({
        propagateKeyDown: handleKeyDown,
      }),
      [handleKeyDown],
    );

    return (
      <ACTrigger
        placement={placement}
        trigger={trigger}
        popupVisible={!disabled && popupVisible}
        onPopupVisibleChange={(visible) => {
          if (disabled) {
            return;
          }
          if (!visible) {
            setSearchText("");
            onSearchTextChange?.("");
          }
          setPopupVisible(visible);
          onPopupVisibleChange(visible);
          if (visible && autoFocus) {
            setTimeout(
              () =>
                showSearch
                  ? inputRef.current?.focus()
                  : containerRef.current?.focus(),
              0,
            );
          }
        }}
        popup={
          <div
            ref={(r) => (containerRef.current = r)}
            className={classNames(
              `ac-dropdown-menu ac-dropdown-layout-${layout}`,
              { "ac-dropdown-menu-mini": mini },
            )}
            style={{ width: menuWidth, minWidth: menuWidth }}
            tabIndex={0}
            onKeyDown={handleKeyDown}
            onMouseDown={onMouseDown}
          >
            {showSearch ? (
              <div className="ac-dropdown-menu-search">
                <Input
                  ref={inputRef}
                  placeholder="Search"
                  onChange={(e) => {
                    const nextSearchText = e.target.value;
                    setSearchText(nextSearchText);
                    onSearchTextChange?.(nextSearchText);
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                  }}
                  value={searchText}
                />
              </div>
            ) : null}
            {trimmedSearchText.length > 0 && itemsToRender.length === 0 ? (
              <div className="ac-dropdown-menu-label text-small-highlight color-supporting">
                No match found
              </div>
            ) : null}
            {itemsToRender.map((item, i) => {
              const key = `item_${i}`;
              if ((item as ACDropdownLabelProps).type === "label") {
                return (
                  <div
                    key={key}
                    className="ac-dropdown-menu-label text-small-highlight color-supporting"
                  >
                    {(item as ACDropdownLabelProps).label}
                  </div>
                );
              }
              if ((item as ACDropdownDividerProps).type === "divider") {
                return <ACDropdownDivider key={key} />;
              }
              const _item = item as ACDropdownItemProps;
              if (_item.items?.length) {
                return (
                  <ACDropdown
                    key={i}
                    items={_item.items}
                    placement="right"
                    trigger="hover"
                    onOptionClick={_onOptionClick}
                    layout={layout}
                    menuWidth={_item.subMenuWidth}
                    filterOption={filterOption}
                  >
                    <ACDropdownItem
                      key={key}
                      onClick={noop}
                      {..._item}
                      secondaryIcon={<ChevronRightIcon />}
                      highlighted={i === highlightedIndex}
                    />
                  </ACDropdown>
                );
              }
              return (
                <ACDropdownItem
                  key={key}
                  onClick={_onOptionClick}
                  {..._item}
                  highlighted={i === highlightedIndex}
                  ref={(el) => (itemRefs.current[i] = el)}
                />
              );
            })}
          </div>
        }
        getPopupContainer={getPopupContainer}
        offsetX={offsetX}
        offsetY={offsetY}
      >
        {isString(child) ? (
          <span
            className={classNames(
              "ac-dropdown-text-trigger",
              {
                "ac-dropdown-text-trigger-disabled": disabled,
              },
              triggerClassName,
            )}
          >
            {child}
            {showArrow ? (
              <>
                {" "}
                {popupVisible ? (
                  <ChevronUpIcon className="ac-dropdown-arrow" />
                ) : (
                  <ChevronDownIcon className="ac-dropdown-arrow" />
                )}
              </>
            ) : null}
          </span>
        ) : (
          child
        )}
      </ACTrigger>
    );
  },
);

export default ACDropdown;

const ACDropdownItem = React.forwardRef<
  HTMLDivElement,
  ACDropdownItemProps &
    ACDropdownItemPrivateProps & { highlighted?: boolean } & Omit<
      React.HTMLAttributes<HTMLDivElement>,
      "onClick"
    >
>(function ACDropdownItem(
  {
    label,
    labelString,
    icon,
    secondaryLabel,
    secondaryIcon,
    value,
    onClick,
    disabled,
    to,
    highlighted,
    tooltip,
    tooltipPlacement,
    ...restdivProps
  },
  ref,
) {
  const _onClick = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (isFunction(onClick)) {
      onClick(value);
    }
  };

  const itemContent = (
    <Tooltip title={tooltip} placement={tooltipPlacement}>
      <div
        className={classNames("ac-dropdown-menu-item", {
          "ac-dropdown-menu-item-disabled": disabled,
          "ac-dropdown-menu-item-highlighted": highlighted,
        })}
        onClick={stopPropagation}
        onMouseDown={disabled || to ? stopPropagation : _onClick}
        ref={ref}
        {...omit(restdivProps, ["filterOption", "labelString"])}
        id={`ac-dropdown-item-${value}`}
      >
        <div className="ac-dropdown-menu-item-primary-section">
          {icon ? (
            <div className="ac-dropdown-menu-item-primary-icon">{icon}</div>
          ) : null}
          <div className="ac-dropdown-menu-item-primary-label">{label}</div>
        </div>
        {secondaryLabel || secondaryIcon ? (
          <div className="ac-dropdown-menu-item-secondary-section">
            {secondaryLabel ? (
              <div className="ac-dropdown-menu-item-secondary-label">
                {secondaryLabel}
              </div>
            ) : null}
            {secondaryIcon ? (
              <div className="ac-dropdown-menu-item-secondary-icon">
                {secondaryIcon}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </Tooltip>
  );
  if (to && !disabled) {
    return (
      <InternalLink className="ac-dropdown-menu-item-link" to={to}>
        {itemContent}
      </InternalLink>
    );
  }
  return itemContent;
});

function ACDropdownDivider() {
  return <div className="ac-dropdown-menu-divider" />;
}

function getFilterOption(
  item: ACDropdownOptionProps,
  dropdownFilterOption?: ACDropdownFilterOption,
) {
  return "filterOption" in item
    ? item.filterOption || dropdownFilterOption
    : dropdownFilterOption;
}

function getLabelSearchText(item: ACDropdownOptionProps) {
  if (!("label" in item)) {
    return "";
  }

  return (
    ("labelString" in item ? item.labelString : "") ||
    (isString(item.label) ? item.label : "")
  );
}

function defaultFilterOption(
  inputValue: string,
  option: ACDropdownOptionProps,
): boolean {
  const label = getLabelSearchText(option);

  if (label.toLowerCase().includes(inputValue)) {
    return true;
  }

  if ("items" in option && option.items?.length) {
    return option.items.some((childOption) =>
      defaultFilterOption(inputValue, childOption),
    );
  }

  return false;
}

function matchesSearchFilter(
  inputValue: string,
  item: ACDropdownOptionProps,
  dropdownFilterOption?: ACDropdownFilterOption,
): boolean {
  const resolvedFilterOption = getFilterOption(item, dropdownFilterOption);

  if (resolvedFilterOption) {
    return resolvedFilterOption(inputValue, item);
  }

  return defaultFilterOption(inputValue, item);
}

const getNextOptionIndex = (
  prevIndex: number,
  items: ACDropdownOptionProps[],
): number => {
  const selectableItems = items.filter(isSelectableOption);
  if (!selectableItems.length) {
    return -1;
  }
  for (let i = prevIndex + 1; i < items.length; i++) {
    const _item = items[i];
    if (isSelectableOption(_item)) {
      return i;
    }
  }
  // return 1st option, meaning no options available after
  return getNextOptionIndex(-1, items);
};
const getPrevOptionIndex = (
  prevIndex: number,
  items: ACDropdownOptionProps[],
): number => {
  const selectableItems = items.filter(isSelectableOption);
  if (!selectableItems.length) {
    return -1;
  }
  for (let i = prevIndex - 1; i > -1; i--) {
    const _item = items[i];
    if (isSelectableOption(_item)) {
      return i;
    }
  }
  // return last option, meaning no options available before
  return getPrevOptionIndex(items.length, items);
};

const isSelectableOption = (item: ACDropdownOptionProps) => {
  const _item = item as ACDropdownItemProps;
  return (
    _item &&
    (_item.label || _item.labelString) &&
    !("type" in _item) &&
    !_item.disabled
  );
};
